import React, { useState, useEffect } from 'react';
import { 
  Eye, 
  Shield, 
  ShieldCheck, 
  UserCheck, 
  UserX, 
  Users, 
  MapPin, 
  Phone, 
  Mail, 
  Calendar, 
  RefreshCw,
  Trash2,
  Heart
} from 'lucide-react';
import {
  Layout,
  SearchBar,
  Pagination,
  FilterDropdown,
  ConfirmDialog,
  LoadingSpinner,
  Alert,
  EmptyState,
  FilterOption
} from '../../components';
import { usePagination, useDebounce, useFilters } from '../../hooks';
import { angelService } from '../../services';
import { Angel } from '../../types';
import { logger } from '../../utils';
import AngelDetailView from './AngelDetailView';

const AngelsPage: React.FC = () => {
  const [angels, setAngels] = useState<Angel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAngel, setSelectedAngel] = useState<Angel | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  const [showFilters, setShowFilters] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Angel | null>(null);

  // Use custom hooks
  const { pagination, setPage, setLimit, setPaginationData } = usePagination(20);
  const { filters, setFilter, clearAllFilters, hasActiveFilters } = useFilters();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);

  const fetchAngels = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }

      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }

      if (filters.verified && filters.verified !== 'all') {
        params.verified = filters.verified;
      }

      const response = await angelService.getAll(params);
      setAngels(response.angels);
      setPaginationData(response.pagination);
    } catch (err) {
      logger.apiError('/admin/angels', err, { component: 'AngelsPage', action: 'fetchAngels' });
      setError('Failed to fetch angels. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAngels();
  }, [pagination.page, pagination.limit, debouncedSearch, filters.status, filters.verified]);

  const handleVerifyAngel = async (angel: Angel, verified: boolean) => {
    try {
      await angelService.verify(angel.id, verified);
      fetchAngels();
    } catch (err) {
      logger.apiError('/admin/angels/verify', err, { component: 'AngelsPage', action: 'verifyAngel' });
      setError('Failed to verify angel. Please try again.');
    }
  };

  const handleStatusChange = async (angel: Angel, isActive: boolean) => {
    try {
      await angelService.updateStatus(angel.id, isActive);
      fetchAngels();
    } catch (err) {
      logger.apiError('/admin/angels/status', err, { component: 'AngelsPage', action: 'updateStatus' });
      setError('Failed to update angel status. Please try again.');
    }
  };

  const handleDeleteAngel = async () => {
    if (!deleteConfirm) return;

    try {
      await angelService.delete(deleteConfirm.id);
      setDeleteConfirm(null);
      fetchAngels();
    } catch (err: any) {
      logger.apiError('/admin/angels/delete', err, { component: 'AngelsPage', action: 'deleteAngel' });
      const errorMessage = err.response?.data?.message || 'Failed to delete angel. Please try again.';
      setError(errorMessage);
      setDeleteConfirm(null);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    clearAllFilters();
    setPage(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (angel: Angel) => {
    const isVerified = angel.user.isVerified;
    const isActive = angel.isActive;

    if (isVerified && isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <ShieldCheck className="w-3 h-3 mr-1" />
          Verified & Active
        </span>
      );
    } else if (isVerified && !isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Shield className="w-3 h-3 mr-1" />
          Verified & Inactive
        </span>
      );
    } else if (!isVerified && isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <UserCheck className="w-3 h-3 mr-1" />
          Active (Unverified)
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <UserX className="w-3 h-3 mr-1" />
          Inactive & Unverified
        </span>
      );
    }
  };

  const statusOptions: FilterOption[] = [
    { label: 'All Status', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ];

  const verifiedOptions: FilterOption[] = [
    { label: 'All Verification', value: 'all' },
    { label: 'Verified', value: 'true' },
    { label: 'Unverified', value: 'false' },
  ];

  if (viewMode === 'detail' && selectedAngel) {
    return (
      <AngelDetailView 
        angel={selectedAngel} 
        onBack={() => {
          setViewMode('list');
          setSelectedAngel(null);
          fetchAngels();
        }}
        onVerify={(verified) => handleVerifyAngel(selectedAngel, verified)}
        onStatusChange={(isActive) => handleStatusChange(selectedAngel, isActive)}
      />
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Angels Management</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage volunteer angels and their verification status ({pagination.total} total)
            </p>
          </div>
          <button
            onClick={fetchAngels}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <SearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search by name, email, phone, or location..."
              className="flex-1"
            />
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Filters {hasActiveFilters && `(${Object.keys(filters).length})`}
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
              <FilterDropdown
                label="Status"
                value={filters.status || 'all'}
                options={statusOptions}
                onChange={(value) => setFilter('status', value)}
                onClear={() => setFilter('status', 'all')}
              />
              
              <FilterDropdown
                label="Verification"
                value={filters.verified || 'all'}
                options={verifiedOptions}
                onChange={(value) => setFilter('verified', value)}
                onClear={() => setFilter('verified', 'all')}
              />
              
              <div className="flex items-end">
                <button
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters && !searchTerm}
                  className="w-full px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>

        {error && (
          <Alert
            variant="error"
            message={error}
            onClose={() => setError(null)}
          />
        )}

        {/* Angels List */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {loading ? (
            <LoadingSpinner className="py-12" message="Loading angels..." />
          ) : angels.length === 0 ? (
            <EmptyState
              icon={<Users className="h-12 w-12 text-gray-400" />}
              title="No angels found"
              message={
                debouncedSearch || hasActiveFilters
                  ? 'Try adjusting your search or filters.'
                  : 'No angels have joined yet.'
              }
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Angel
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Strayyos
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Joined
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {angels.map((angel) => (
                      <tr key={angel.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                                <Heart className="h-5 w-5 text-orange-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {angel.user.name || 'Unnamed Angel'}
                              </div>
                              {angel.location && (
                                <div className="text-sm text-gray-500 flex items-center">
                                  <MapPin className="h-3 w-3 mr-1" />
                                  {angel.location}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {angel.user.email && (
                              <div className="flex items-center mb-1">
                                <Mail className="h-3 w-3 mr-1 text-gray-400" />
                                {angel.user.email}
                              </div>
                            )}
                            {angel.user.phone && (
                              <div className="flex items-center">
                                <Phone className="h-3 w-3 mr-1 text-gray-400" />
                                {angel.user.phone}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(angel)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {angel._count?.strayyos || 0} assigned
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500 flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {formatDate(angel.createdAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => {
                                setSelectedAngel(angel);
                                setViewMode('detail');
                              }}
                              className="text-orange-600 hover:text-orange-900 p-1 rounded"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            
                            {!angel.user.isVerified ? (
                              <button
                                onClick={() => handleVerifyAngel(angel, true)}
                                className="text-green-600 hover:text-green-900 p-1 rounded"
                                title="Verify Angel"
                              >
                                <ShieldCheck className="h-4 w-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleVerifyAngel(angel, false)}
                                className="text-yellow-600 hover:text-yellow-900 p-1 rounded"
                                title="Unverify Angel"
                              >
                                <Shield className="h-4 w-4" />
                              </button>
                            )}

                            {angel.isActive ? (
                              <button
                                onClick={() => handleStatusChange(angel, false)}
                                className="text-gray-600 hover:text-gray-900 p-1 rounded"
                                title="Deactivate Angel"
                              >
                                <UserX className="h-4 w-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleStatusChange(angel, true)}
                                className="text-green-600 hover:text-green-900 p-1 rounded"
                                title="Activate Angel"
                              >
                                <UserCheck className="h-4 w-4" />
                              </button>
                            )}

                            <button
                              onClick={() => setDeleteConfirm(angel)}
                              className="text-red-600 hover:text-red-900 p-1 rounded"
                              title="Delete Angel"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.pages}
                totalItems={pagination.total}
                itemsPerPage={pagination.limit}
                onPageChange={setPage}
                onPageSizeChange={setLimit}
                pageSizeOptions={[10, 20, 50, 100]}
                showPageSize={true}
              />
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDeleteAngel}
        title="Delete Angel"
        message={`Are you sure you want to delete angel "${deleteConfirm?.user.name || deleteConfirm?.user.email}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmButtonVariant="danger"
      />
    </Layout>
  );
};

export default AngelsPage;
